import { Link } from "react-router-dom";
import { BookOpen, CreditCard, Download, Key, LayoutDashboard, Shield } from "lucide-react";
import SecretKey from "@/components/SecretKey";
import { LicenseStatus, UpgradeToBundleButton, CancelRenewalButton } from "@/components/DashboardExtras";

const btn = "w-full inline-flex items-center justify-center gap-2 rounded-lg text-sm font-medium px-3 py-2.5 transition-colors disabled:opacity-50";
const fmt = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });

/** Carte d'un produit actif du tableau de bord : infos, licence, actions. */
export default function ProductCard({ bot, display, products, onDownload, downloading, onRegenerate, regenerating, onRefresh, navigate }) {
  const lifetime = !bot.expires_at || new Date(bot.expires_at).getFullYear() > 2090 || bot.plan_id === "lifetime";
  const daysLeft = bot.expires_at ? Math.ceil((new Date(bot.expires_at) - new Date()) / 86400000) : null;
  const soon = !lifetime && daysLeft !== null && daysLeft <= 7;
  // Dernière version publiée du produit (Panel admin > Boutique), sinon celle du nom du fichier.
  const version = bot.latest_version ? `v${String(bot.latest_version).replace(/^v/i, "")}` : (bot.download_filename || "").match(/v\d+(\.\d+)+/)?.[0];
  const isPhone = (bot.bot_type || "").startsWith("vantaphone-");
  const isGuard = bot.bot_type === "vantaguard";
  const licenceState = bot.license_active === false ? ["Désactivée", "bg-red-500/10 text-red-300 border-red-500/20"]
    : bot.license_bound ? ["Activée", "bg-emerald-500/10 text-emerald-300 border-emerald-500/20"]
    : ["Non activée", "bg-amber-500/10 text-amber-200 border-amber-500/20"];

  return (
    <div data-testid={`bot-card-${bot.id}`} className="group flex flex-col bg-[#0f1019] border border-white/[0.07] hover:border-white/15 hover:-translate-y-0.5 hover:shadow-[0_24px_60px_-30px_rgba(59,130,246,0.45)] rounded-2xl p-5 transition-all duration-300">
      {/* En-tête */}
      <div className="flex items-start gap-3">
        <img src={bot.bot_avatar_url || display.logo} onError={(e) => { if (display.logo && !e.currentTarget.src.endsWith(display.logo)) e.currentTarget.src = display.logo; }} alt="" className="w-12 h-12 rounded-xl object-cover ring-1 ring-white/10 shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-semibold text-base leading-tight truncate" title={bot.bot_name}>{bot.bot_name}</h3>
          <p className={`text-xs mt-0.5 truncate ${display.colorClass}`}>{bot.bot_type_name}</p>
        </div>
        {bot.is_trial
          ? <span data-testid={`trial-badge-${bot.id}`} className="shrink-0 text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">Essai</span>
          : <span className="shrink-0 inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Actif</span>}
      </div>

      {/* Offre et échéance */}
      <div className="grid grid-cols-2 gap-2 mt-4">
        <div className="rounded-xl bg-black/20 px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wider text-white/35">Offre</p>
          <p className="text-sm mt-0.5 truncate">{bot.plan_name}</p>
        </div>
        <div className="rounded-xl bg-black/20 px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wider text-white/35">{lifetime ? "Durée" : "Expire le"}</p>
          <p className={`text-sm mt-0.5 truncate ${soon ? "text-amber-300" : lifetime ? "text-emerald-300" : ""}`}>
            {lifetime ? "À vie" : <>{fmt(bot.expires_at)}<span className="text-white/35 text-xs"> · {daysLeft} j</span></>}
          </p>
        </div>
      </div>

      {/* Script : licence et téléchargement */}
      {bot.downloadable && (
        <div className="mt-3 rounded-xl bg-black/20 border border-white/5 p-3 space-y-2.5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-white/50 flex items-center gap-1.5"><Key className="w-3.5 h-3.5" />Clé de licence</span>
            <span className={`text-[11px] px-2 py-0.5 rounded-full border ${licenceState[1]}`}>{licenceState[0]}</span>
          </div>
          {bot.license_key && (
            <SecretKey
              value={bot.license_key}
              testid={`license-${bot.id}`}
              regenerating={regenerating}
              onRegenerate={bot.license_active !== false ? () => onRegenerate(bot.id) : undefined}
            />
          )}
          {bot.license_key && <LicenseStatus bot={bot} />}
        </div>
      )}

      {/* Actions */}
      <div className="mt-3 space-y-2">
        {bot.downloadable && bot.download_url && (
          <button data-testid={`download-bot-${bot.id}`} onClick={() => onDownload(bot)} disabled={downloading} title={bot.download_filename}
            className={`${btn} bg-blue-600 hover:bg-blue-500 text-white`}>
            <Download className="w-4 h-4" />{downloading ? "Préparation…" : "Télécharger"}
            {version && !downloading && <span className="text-[11px] px-1.5 py-0.5 rounded bg-white/15">{version}</span>}
          </button>
        )}
        {isGuard && (
          <Link to={`/anticheat/${bot.id}`} data-testid={`anticheat-bot-${bot.id}`} className={`${btn} bg-red-600 hover:bg-red-500 text-white`}>
            <Shield className="w-4 h-4" />Configurer l'anti-cheat
          </Link>
        )}
        {isPhone && (
          <Link to="/docs/vantaphone" data-testid={`docs-bot-${bot.id}`} className={`${btn} bg-white/5 hover:bg-white/10 border border-white/10 text-white/90`}>
            <BookOpen className="w-4 h-4" />Documentation et installation
          </Link>
        )}
        {bot.downloadable && bot.is_trial && (
          <button data-testid={`buy-trial-${bot.id}`} onClick={() => navigate(`/bot/${bot.bot_type}`)} className={`${btn} bg-emerald-600/15 hover:bg-emerald-600/25 border border-emerald-500/30 text-emerald-100`}>
            <CreditCard className="w-4 h-4" />Acheter (la clé reste la même)
          </button>
        )}
        {bot.downloadable && <UpgradeToBundleButton bot={bot} products={products} />}

        {!bot.downloadable && (
          <Link to={`/bot-dashboard/${bot.id}`} data-testid={`dash-bot-link-${bot.id}`} className={`${btn} bg-violet-600 hover:bg-violet-500 text-white`}>
            <LayoutDashboard className="w-4 h-4" />Ouvrir le tableau de bord
          </Link>
        )}
        {!bot.downloadable && (
          <Link to="/tutorial" data-testid={`tutorial-bot-${bot.id}`} className="block text-center text-xs text-white/40 hover:text-white py-1">
            Comment créer et inviter un bot Discord ?
          </Link>
        )}
      </div>

      {/* Pied : arrêt du renouvellement automatique */}
      {bot.auto_renew && (
        <div className="mt-auto pt-4">
          <div className="border-t border-white/5 pt-3"><CancelRenewalButton bot={bot} onDone={onRefresh} /></div>
        </div>
      )}
    </div>
  );
}
